import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../lib/api';
import LoginSheet from '../components/LoginSheet';
import { track, identify } from '../lib/analytics';

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [promptReason, setPromptReason] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      identify(res.data?.user_id, { name: res.data?.name });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    track('login_started');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/app';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* noop */ }
    setUser(null);
    window.location.href = '/';
  };

  // Soft-login prompt: call from anywhere when a guest hits a gated action.
  const promptLogin = useCallback((reason) => setPromptReason(reason || ''), []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, refresh: checkAuth, promptLogin }}>
      {children}
      {promptReason !== null && (
        <LoginSheet reason={promptReason} onClose={() => setPromptReason(null)} onLogin={login} />
      )}
    </AuthContext.Provider>
  );
}
